import { useRef, useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';

import { SET_WELCOME_DIALOGUE_OPEN, TRIAL_PROJECT_LIMIT, TRIAL_SCHEDULE_LIMIT, TRIAL_ADDITIONAL_USER_LIMIT, SET_STARTUP_WELCOME_ENABLED,
  SET_EDIT_SUBSCRIPTION_MANAGERS_DIALOGUE_OPEN, SET_EDIT_PROJECT_DIALOGUE_ID, ADD_PROJECT, SiteboardApiValidator, isSubscriptionStarted,
  SET_NEW_PROJECT_DIALOGUE_SHOWING,
} from '../actions/types';
import { siteboardAction, setAlert } from '../actions/defaultActions';
import { millisDurationToString, keyCountInObject, objectValuesToList } from '../Util';

import PopupWindow from './PopupWindow';
import SubscriptionCard from './SubscriptionCard';
import PushButton from './PushButton';

// icon imports
import {
  CircleCheckIcon,
  FolderPlusIcon,
  UsersIcon,
  ArrowRightIcon,
  LightbulbIcon,
  SparklesIcon,
  PartyPopperIcon,
  ShieldCheckIcon,
} from 'lucide-react';
import AddIcon from '../icons/addIcon';
import UpgradeIcon from '../icons/upgradeIcon';
import CheckboxIcon from '../icons/checkboxIcon';

// test states
const testNoProjects = false;
const testShowAssignProjectManagers = false;
const testShowOnStartup = false;

const WelcomeDialogue = ({ _users, _userId, _subscriptions, _userPreferences, _projects, siteboardAction, setAlert }) => {
  const [showOnStartup, setShowOnStartup] = useState(_userPreferences.showWelcomeScreen === true || testShowOnStartup === true);

  const closeDialogue = useCallback(() => {
    siteboardAction(SET_WELCOME_DIALOGUE_OPEN, { isOpen: false });
  }, [siteboardAction]);

  const helpSymbolJSX = (<div className='inline-block'><div className='flex justify-center bg-[#222] border border-[#888] rounded-full w-[1.6em] h-[1.6em] mx-1'>?</div></div>);

  let hasNoProjectManagers = true;
  let ownedSubscription = null;
  const joinedSubscriptions = [];
  for(const subscriptionId in _subscriptions) {
    const subscription = _subscriptions[subscriptionId];
    if(!(_userId in subscription.members)) continue;
    if(!isSubscriptionStarted(subscription)) continue;

    if(subscription.members[_userId].role === 'Owner') {
      ownedSubscription = subscription;
      for(const userId in ownedSubscription.members) {
        if(ownedSubscription.members[userId].role === 'Admin') {
          hasNoProjectManagers = false;
          break;
        }
      }
    } else if(subscription.members[_userId].role === 'Admin' || subscription.members[_userId].role === 'Member') {
      joinedSubscriptions.push(subscription);
    }
  }
  let showAssignProjectManagers = ownedSubscription !== null && hasNoProjectManagers;
  if(testShowAssignProjectManagers) showAssignProjectManagers = true;

  let userCanCreateProjectsForEmptySubscription = false;
  for(const subscription of objectValuesToList(_subscriptions)) {
    if(!SiteboardApiValidator[ADD_PROJECT].isUserIdAuthorized(_userId, subscription, null)) continue;
    if(keyCountInObject(subscription.projectIds) > 0) continue;
    userCanCreateProjectsForEmptySubscription = true;
  }
  if(testNoProjects) userCanCreateProjectsForEmptySubscription = true;

  const subscriptionIsActiveNotice = isSubscriptionStarted(ownedSubscription) ? `Your ${ownedSubscription.type} Subscription is now active` : null;    

  const footer = () => {
    return (
      <div className='flex justify-end w-full text-right'>
        {/* Dont show on startup checkbox */}
        <button 
          className='flex justify-end items-center gap-2 text-white px-6 py-2'
          onClick={() => {
            //if(showOnStartup) {
            //  setAlert('You can access this welcome screen at any time by clicking the "?" symbol in the bottom right corner of your screen.');
            //}
            setShowOnStartup(!showOnStartup);
            siteboardAction(SET_STARTUP_WELCOME_ENABLED, {enabled: !showOnStartup});
          }}
        >
          <CheckboxIcon isChecked={showOnStartup} size='1.8em' color='#fff' />
          <div>Show Welcome Page on Startup</div>
        </button>
      </div>
    );
  };

  return (
    <PopupWindow
      title={`Welcome to ${process.env.REACT_APP_WEBSITE_NAME}!`}
      closeDialogue={closeDialogue}
      bgStyle="bg-[#292929]"
      footer={footer}
    >
      <div className="w-full max-w-screen-lg text-white">

        <div className="flex flex-col gap-8">

          {/* Subscription activation celebration */}
          {subscriptionIsActiveNotice !== null && (
            <div className="
              relative isolate overflow-hidden
              rounded-2xl
              border border-emerald-400/25
              bg-gradient-to-br
              from-emerald-500/[0.12]
              via-[#202c29]
              to-[#202323]
              px-6 py-8 sm:px-8
              animate-in fade-in slide-in-from-bottom-4
              duration-700
            ">

              {/* Ambient glow */}
              <div className="
                pointer-events-none absolute
                -top-24 -right-20
                h-64 w-64 rounded-full
                bg-emerald-400/10 blur-3xl
              " />

              <div className="
                relative flex flex-col
                items-center gap-5 text-center
                sm:flex-row sm:text-left
              ">

                {/* Animated success icon */}
                <div className="relative shrink-0">

                  {/* Pulsing halo */}
                  <div className="
                    absolute inset-0 rounded-full
                    bg-emerald-400/20
                    animate-ping
                    [animation-duration:2.5s]
                    motion-reduce:animate-none
                  " />

                  <div className="
                    relative flex h-20 w-20
                    items-center justify-center
                    rounded-full
                    border border-emerald-400/30
                    bg-emerald-400/10
                    shadow-[0_0_40px_rgba(52,211,153,0.16)]
                    animate-in zoom-in-50 spin-in-12
                    duration-700
                  ">
                    <CircleCheckIcon
                      size={42}
                      strokeWidth={1.7}
                      className="text-emerald-400"
                    />
                  </div>

                  {/* Floating sparkles */}
                  <SparklesIcon
                    size={19}
                    className="
                      absolute -top-3 -right-4
                      text-[#f9a928]
                      animate-bounce
                      [animation-duration:2.2s]
                      motion-reduce:animate-none
                    "
                  />

                  <SparklesIcon
                    size={13}
                    className="
                      absolute -bottom-2 -left-4
                      text-emerald-300
                      animate-pulse
                      [animation-duration:1.8s]
                      motion-reduce:animate-none
                    "
                  />

                  <div className="
                    absolute -top-2 -left-5
                    h-1.5 w-1.5 rounded-full
                    bg-[#f9a928]
                    animate-ping
                    [animation-duration:2s]
                    motion-reduce:animate-none
                  " />

                  <div className="
                    absolute -bottom-3 -right-5
                    h-2 w-2 rounded-full
                    bg-emerald-400
                    animate-ping
                    [animation-delay:400ms]
                    [animation-duration:2.4s]
                    motion-reduce:animate-none
                  " />

                </div>

                <div className="flex flex-1 flex-col gap-2">

                  <div className="
                    flex items-center justify-center
                    gap-2 sm:justify-start
                  ">
                    <PartyPopperIcon
                      size={16}
                      className="text-[#f9a928]"
                    />
                    <span className="
                      text-xs font-bold uppercase
                      tracking-[0.16em]
                      text-emerald-400
                    ">
                      You're all set!
                    </span>
                  </div>

                  <h2 className="
                    text-2xl sm:text-3xl
                    font-bold tracking-tight
                    text-white
                  ">
                    Account Activated!
                  </h2>

                  <p className="
                    text-[0.95em] leading-relaxed
                    text-white/70
                  ">
                    {subscriptionIsActiveNotice}
                  </p>

                </div>

              </div>

              {/* Bottom accent */}
              <div className="
                absolute inset-x-0 bottom-0 h-px
                bg-gradient-to-r
                from-transparent
                via-emerald-400/50
                to-transparent
              " />

            </div>
          )}

          {/* Next steps */}
          {(userCanCreateProjectsForEmptySubscription ||
            showAssignProjectManagers) && (

            <section className="flex flex-col gap-5">

              <div className="flex flex-col gap-1">

                <div className="
                  flex items-center gap-2
                  text-[#f9a928]
                ">
                  <span className="
                    text-xs font-semibold uppercase
                    tracking-[0.14em]
                  ">
                    Getting Started
                  </span>
                </div>

                <h2 className="
                  text-xl sm:text-2xl
                  font-bold tracking-tight
                ">
                  Let's get you up and running
                </h2>

                <p className="text-sm text-white/55">
                  A couple of quick steps to make the most
                  of your account.
                </p>

              </div>

              <div className="
                grid grid-cols-1 gap-4
                md:grid-cols-2
              ">

                {/* Create first project */}
                {userCanCreateProjectsForEmptySubscription && (

                  <div className="
                    group relative flex flex-col
                    overflow-hidden rounded-xl
                    border border-white/10
                    bg-[#222]
                    p-6
                    transition-all duration-300
                    hover:-translate-y-1
                    hover:border-[#471cff]/60
                    hover:shadow-[0_12px_35px_rgba(71,28,255,0.12)]
                  ">

                    <div className="
                      absolute top-0 left-0 right-0
                      h-[2px]
                      bg-gradient-to-r
                      from-[#471cff] to-[#916fff]
                      opacity-60
                      transition-opacity duration-300
                      group-hover:opacity-100
                    " />

                    <div className="
                      mb-5 flex h-12 w-12
                      items-center justify-center
                      rounded-xl
                      bg-[#471cff]/15
                      text-[#a18aff]
                      transition-all duration-300
                      group-hover:scale-110
                      group-hover:bg-[#471cff]/25
                    ">
                      <FolderPlusIcon
                        size={24}
                        strokeWidth={1.8}
                      />
                    </div>

                    <div className="mb-5 flex-1">

                      <div className="
                        mb-2 flex items-center gap-2
                      ">
                        <span className="
                          text-[11px] font-bold
                          uppercase tracking-wider
                          text-[#a18aff]
                        ">
                          Step 01
                        </span>
                      </div>

                      <h3 className="
                        mb-2 text-lg font-bold
                        tracking-tight
                      ">
                        Create Your First Project
                      </h3>

                      <p className="
                        text-sm leading-relaxed
                        text-white/60
                      ">
                        Start by creating a project to organize
                        your 3-week schedules. Invite team
                        members to collaborate and keep
                        everyone on the same page.
                      </p>

                    </div>

                    <PushButton
                      textColor="white"
                      bgColor="#471cff"
                      hoverBgColor="#603aff"
                      onClick={() => {
                        siteboardAction(SET_NEW_PROJECT_DIALOGUE_SHOWING, { isOpen: true });
                      }}
                    >
                      <div className="
                        flex items-center justify-center
                        gap-2
                      ">
                        <FolderPlusIcon size={17} />
                        <span>Create Project</span>
                        <ArrowRightIcon size={16} />
                      </div>
                    </PushButton>

                  </div>
                )}

                {/* Assign account managers */}
                {showAssignProjectManagers && (

                  <div className="
                    group relative flex flex-col
                    overflow-hidden rounded-xl
                    border border-white/10
                    bg-[#222]
                    p-6
                    transition-all duration-300
                    hover:-translate-y-1
                    hover:border-[#f9a928]/40
                    hover:shadow-[0_12px_35px_rgba(249,169,40,0.08)]
                  ">

                    <div className="
                      absolute top-0 left-0 right-0
                      h-[2px]
                      bg-gradient-to-r
                      from-[#f9a928] to-[#ffd37d]
                      opacity-40
                      transition-opacity duration-300
                      group-hover:opacity-100
                    " />

                    <div className="
                      mb-5 flex h-12 w-12
                      items-center justify-center
                      rounded-xl
                      bg-[#f9a928]/10
                      text-[#f9a928]
                      transition-all duration-300
                      group-hover:scale-110
                      group-hover:bg-[#f9a928]/20
                    ">
                      <UsersIcon
                        size={24}
                        strokeWidth={1.8}
                      />
                    </div>

                    <div className="mb-5 flex-1">

                      <div className="
                        mb-2 flex items-center gap-2
                      ">
                        <span className="
                          text-[11px] font-bold
                          uppercase tracking-wider
                          text-[#f9a928]
                        ">
                          Step 02
                        </span>

                        <span className="
                          rounded-full
                          border border-white/10
                          bg-white/5
                          px-2 py-0.5
                          text-[10px] font-medium
                          text-white/50
                        ">
                          Optional
                        </span>
                      </div>

                      <h3 className="
                        mb-2 text-lg font-bold
                        tracking-tight
                      ">
                        Invite Account Managers
                      </h3>

                      <p className="
                        text-sm leading-relaxed
                        text-white/60
                      ">
                        Bring colleagues on board to help
                        manage projects. Account managers
                        can create, edit, and assign members
                        across all projects in your account.
                      </p>

                    </div>

                    <PushButton
                      textColor="white"
                      bgColor="#383838"
                      hoverBgColor="#484848"
                      onClick={() => {
                        siteboardAction(
                          SET_EDIT_SUBSCRIPTION_MANAGERS_DIALOGUE_OPEN,
                          { isOpen: true }
                        );
                      }}
                    >
                      <div className="
                        flex items-center justify-center
                        gap-2
                      ">
                        <UsersIcon size={17} />
                        <span>Assign Account Managers</span>
                        <ArrowRightIcon size={16} />
                      </div>
                    </PushButton>

                  </div>
                )}

              </div>

            </section>
          )}

          {/* Joined subscriptions */}
          {ownedSubscription === null &&
            joinedSubscriptions.length > 0 && (

            <section className="flex flex-col gap-4">

              <div className="flex items-center gap-3">

                <div className="
                  flex h-10 w-10
                  items-center justify-center
                  rounded-lg bg-white/5
                  text-white/70
                ">
                  <ShieldCheckIcon size={21} />
                </div>

                <div>
                  <h2 className="
                    text-lg font-bold tracking-tight
                  ">
                    Accounts You've Joined
                  </h2>

                  <p className="text-xs text-white/50">
                    Your connected workspaces
                  </p>
                </div>

              </div>

              <div className="flex flex-col gap-3">
                {joinedSubscriptions.map(
                  (otherSubscription, index) => (
                    <SubscriptionCard
                      key={index}
                      subscription={otherSubscription}
                    />
                  )
                )}
              </div>

            </section>
          )}

          {/* Feedback */}
          <section className="
            relative flex flex-col gap-4
            rounded-xl
            border border-white/[0.08]
            bg-white/[0.035]
            p-5 sm:flex-row
            sm:items-start sm:gap-4
          ">

            <div className="
              flex h-11 w-11 shrink-0
              items-center justify-center
              rounded-xl
              bg-[#f9a928]/10
              text-[#f9a928]
            ">
              <LightbulbIcon
                size={22}
                strokeWidth={1.8}
              />
            </div>

            <div className="flex flex-col gap-2">

              <h3 className="
                text-base font-semibold
                tracking-tight
              ">
                Help Us Make It Better
              </h3>

              <div className="
                text-sm leading-relaxed
                text-white/55
              ">
                Have an idea or feature request?
                We'd love to hear it. Click the{' '}
                <span className="
                  inline-flex items-center
                  align-middle
                  text-white
                ">
                  {helpSymbolJSX}
                </span>
                {' '}icon in the bottom-right corner
                of your screen to share your feedback.
              </div>

            </div>

          </section>

        </div>

      </div>
    </PopupWindow>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
  _userPreferences: state.serverSynced.userPreferences,
  _projects: state.serverSynced.projects,
}};
export default connect(mapStateToProps, { siteboardAction, setAlert })(WelcomeDialogue);
